/**
 * Bulk User Lookup Component
 * 
 * Input a list of emails and get back user details from Microsoft Graph
 */

import React, { useState, useCallback } from 'react';
import GraphClientService, { GraphUser } from '../services/graphClient';

interface UserResult {
  email: string;
  user: GraphUser | null;
  error?: string;
}

export const BulkUserLookup: React.FC = () => {
  const [emailInput, setEmailInput] = useState<string>('');
  const [results, setResults] = useState<UserResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const graphService = GraphClientService.getInstance();

  const handleLookup = useCallback(async () => {
    const emails = emailInput
      .split(/[\n,;]/)
      .map(e => e.trim())
      .filter(e => e.length > 0 && e.includes('@'));

    if (emails.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const fields = [
        'id', 'displayName', 'mail', 'userPrincipalName',
        'givenName', 'surname', 'jobTitle', 'department',
        'officeLocation', 'city', 'state', 'country', 'postalCode',
        'streetAddress', 'companyName', 'employeeId', 'employeeType',
        'mobilePhone', 'businessPhones', 'faxNumber',
        'accountEnabled', 'createdDateTime', 'lastSignInDateTime',
        'onPremisesSamAccountName', 'onPremisesDistinguishedName',
        'preferredLanguage', 'usageLocation', 'mailNickname'
      ];
      
      const lookupResults = await graphService.getUsersByEmails(emails, fields);
      setResults(lookupResults);
    } catch (err: any) {
      setError(err.message || 'Failed to look up users');
    } finally {
      setLoading(false);
    }
  }, [emailInput, graphService]);

  const exportToCSV = useCallback(() => {
    if (results.length === 0) return;

    const headers = [
      'Email', 'Display Name', 'First Name', 'Last Name', 'Job Title', 'Department',
      'Company', 'Employee ID', 'Employee Type', 'Office Location', 'Street Address',
      'City', 'State', 'Postal Code', 'Country', 'Mobile Phone', 'Business Phone',
      'Fax', 'User Principal Name', 'Mail Nickname', 'SAM Account Name',
      'Account Enabled', 'Usage Location', 'Preferred Language', 'Status'
    ];
    const rows = results.map(r => [
      r.email,
      r.user?.displayName || '',
      r.user?.givenName || '',
      r.user?.surname || '',
      r.user?.jobTitle || '',
      r.user?.department || '',
      r.user?.companyName || '',
      r.user?.employeeId || '',
      r.user?.employeeType || '',
      r.user?.officeLocation || '',
      r.user?.streetAddress || '',
      r.user?.city || '',
      r.user?.state || '',
      r.user?.postalCode || '',
      r.user?.country || '',
      r.user?.mobilePhone || '',
      r.user?.businessPhones?.[0] || '',
      r.user?.faxNumber || '',
      r.user?.userPrincipalName || '',
      r.user?.mailNickname || '',
      r.user?.onPremisesSamAccountName || '',
      r.user?.accountEnabled?.toString() || '',
      r.user?.usageLocation || '',
      r.user?.preferredLanguage || '',
      r.user ? 'Found' : r.error || 'Not Found'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `user_lookup_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [results]);

  const foundCount = results.filter(r => r.user).length;
  const notFoundCount = results.filter(r => !r.user).length;

  return (
    <div className="bg-white shadow rounded-lg overflow-visible">
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
        <h2 className="text-xl font-semibold text-white">Bulk User Lookup</h2>
        <p className="text-purple-100 text-sm mt-1">Enter email addresses to look up user details</p>
      </div>

      <div className="p-6 space-y-4">
        {/* Input Area */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Addresses
            <span className="text-gray-500 font-normal ml-2">(one per line, or comma/semicolon separated)</span>
          </label>
          <textarea
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="user1@unifirst.com&#10;user2@unifirst.com&#10;user3@unifirst.com"
            rows={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 font-mono text-sm bg-white text-gray-900"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleLookup}
            disabled={loading || !emailInput.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Looking up...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Look Up Users
              </>
            )}
          </button>

          {results.length > 0 && (
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Results Summary */}
        {results.length > 0 && (
          <div className="bg-gray-50 rounded-md p-4">
            <div className="flex gap-6 text-sm">
              <span className="text-gray-600">
                Total: <strong>{results.length}</strong>
              </span>
              <span className="text-green-600">
                Found: <strong>{foundCount}</strong>
              </span>
              <span className="text-red-600">
                Not Found: <strong>{notFoundCount}</strong>
              </span>
            </div>
          </div>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Email</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Display Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">First Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Last Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Job Title</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Department</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Company</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Employee ID</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Office</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">City</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">State</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Country</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Mobile</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Business Phone</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Account Enabled</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((result, idx) => (
                  <tr key={idx} className={result.user ? 'hover:bg-gray-50' : 'bg-red-50'}>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                      {result.user ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Found
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {result.error || 'Not Found'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 font-mono whitespace-nowrap">{result.email}</td>
                    <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{result.user?.displayName || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.givenName || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.surname || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.jobTitle || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.department || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.companyName || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.employeeId || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.officeLocation || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.city || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.state || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.country || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.mobilePhone || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{result.user?.businessPhones?.[0] || '-'}</td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                      {result.user?.accountEnabled === true ? (
                        <span className="text-green-600">Yes</span>
                      ) : result.user?.accountEnabled === false ? (
                        <span className="text-red-600">No</span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUserLookup;
